import React, { Component, RefObject } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import Banner from '@atlaskit/banner';
import ErrorIcon from '@atlaskit/icon/glyph/error';

import { NavigateButtons, TestCases, Welcome } from '@qality/common-components';
import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { messages } from '@qality/common-messages';
import {
  TestCase,
  TestCasesCollection,
  TestStepsView,
} from '@qality/common-model';
import { TestCaseFullScreenCloseCallbackData } from '@qality/common-model/src/TestCaseFullScreenData';
import { TestTypeProvider } from '@qality/common-pages/src/TestTypesPage/TestTypesProvider';
import {
  StorageOfUniqueServices,
  TestCasesHelper,
  TestCasesService,
  ViewService,
} from '@qality/common-service';
import { QAlityIssueViewContext } from '@qality/common-state';
import { ExpandUtils } from '@qality/common-utils';

import IssueTestTypeProvider from './IssueTestTypeProvider';
import './_qality-issue-view.scss';

type Props = Record<string, never>;

type State = {
  isLoading: boolean;
  isFetchingError: boolean;
  isAddingTestCase: boolean;
  testCases: TestCasesCollection;
  issueKey: string;
  view: TestStepsView;
  expanded: boolean;
  testCaseIds: number[];
};

export default class QAlityIssueView extends Component<Props, State> {
  static contextType = QAlityIssueViewContext;
  containerRef: RefObject<HTMLDivElement>;
  constructor(props: Props) {
    super(props);
    this.containerRef = React.createRef();
    StorageOfUniqueServices.get('ResizeService').init(this.containerRef);
    this.state = {
      view: null,
      expanded: false,
      testCases: new TestCasesCollection(),
      issueKey: '',
      isLoading: true,
      isFetchingError: false,
      isAddingTestCase: false,
      testCaseIds: [],
    };
  }

  async componentDidMount(): Promise<void> {
    const issueKey = this.context.currentIssue.key;
    try {
      await StorageOfUniqueServices.get('AttachmentConfigService').init();
      const testCases: TestCasesCollection =
        await TestCasesService.fetchTestCases(this.context.currentIssue);
      if (!testCases.isEmpty()) {
        this.setState({
          testCases: testCases,
          isLoading: false,
          issueKey,
          view: ViewService.fetchView(issueKey),
          expanded: TestCasesHelper.checkAllExpanded(testCases),
          testCaseIds: testCases.testCases.map((testCase) =>
            Number(testCase.id)
          ),
        });
      } else {
        this.setState({
          testCases: new TestCasesCollection(),
          isLoading: false,
          issueKey,
        });
      }
    } catch (e) {
      this.setState({
        isFetchingError: true,
        isLoading: false,
        issueKey,
      });
    }
  }
  addTestCase = async (): Promise<void> => {
    this.setState({ isAddingTestCase: true });
    const testCases = await TestCasesService.addNewTestCase(
      this.state.issueKey,
      this.state.testCases,
      this.removeNotCreatedTestCase,
      this.updateCreatedTestCase,
      this.updateTestCaseIdsState
    );
    this.setState({ testCases, isAddingTestCase: false });
  };

  updateTestCaseIdsState = (testCaseId: number): void => {
    this.setState((prevState) => {
      return {
        testCaseIds: [...prevState.testCaseIds, Number(testCaseId)],
      };
    });
  };

  updateCreatedTestCase = (testCaseId: number, testCaseName: string): void => {
    const testCases = cloneDeep(this.state.testCases);
    const testCaseIndex = findIndex(testCases.testCases, function (testCase) {
      return testCase.id === testCaseId;
    });

    testCases.testCases[testCaseIndex].name = testCaseName;

    this.setState({ testCases }, () => {
      StorageOfUniqueServices.get('ResizeService').resize();
    });
  };

  removeNotCreatedTestCase = (testCaseId: number): void => {
    const testCases = cloneDeep(this.state.testCases);
    const testCaseIndex = findIndex(testCases.testCases, function (testCase) {
      return testCase.id === testCaseId;
    });
    testCases.testCases.splice(testCaseIndex, 1);
    this.setState({ testCases }, () => {
      StorageOfUniqueServices.get('ResizeService').resize();
    });
  };

  changeView = (value: TestStepsView): void => {
    ViewService.saveView(this.state.issueKey, value);
    this.setState({ view: value });
  };

  onDeleteTestCaseLink = (testCaseId: number): void => {
    const updatedTestCasesIds = this.state.testCaseIds.filter(
      (id) => id !== testCaseId
    );
    this.setState({ testCaseIds: updatedTestCasesIds });
  };

  expandTestCases = (): void => {
    const newExpanded = !this.state.expanded;
    const newTestCases = this.state.testCases.testCases.map<TestCase>(
      (item) => {
        item.expanded = newExpanded;
        return item;
      }
    );
    this.setState({
      testCases: new TestCasesCollection(newTestCases),
      expanded: newExpanded,
    });
    ExpandUtils.expandAllTestCases(
      this.state.issueKey,
      this.state.testCases,
      newExpanded
    );
  };

  expandOneTestCase = (testCaseIndex: number, value: boolean): void => {
    const newTestCase = this.state.testCases.testCases[testCaseIndex];
    newTestCase.expanded = value;
    const newTestCasesList = this.state.testCases;
    newTestCasesList.testCases[testCaseIndex] = newTestCase;
    this.setState({
      testCases: newTestCasesList,
      expanded: TestCasesHelper.checkAllExpanded(newTestCasesList),
    });

    const { issueKey } = this.state;
    ExpandUtils.updateExpandValue(issueKey, newTestCase, value);
  };

  getErrorFetchingBanner(): JSX.Element {
    return (
      <Banner
        icon={<ErrorIcon label='Error icon' secondaryColor='inherit' />}
        appearance='error'
      >
        {messages['error.qalityIssueView.fetching.message']}
      </Banner>
    );
  }

  updateTestCases = (testCases: TestCasesCollection): void => {
    this.setState({
      testCases,
    });
  };

  syncDataFromFullScreen = ({
    testCase,
  }: TestCaseFullScreenCloseCallbackData): void => {
    const testCaseIndex = this.state.testCases.testCases.findIndex(
      (testCaseCurrent) => testCaseCurrent.id === testCase.id
    );

    const testCases = this.state.testCases;
    testCases.testCases[testCaseIndex] = testCase;

    this.setState({
      testCases,
    });
  };

  getQAlityIssueViewContent(): JSX.Element {
    if (this.showLoading()) {
      return <Loading relative={true} spinnerSize='large' fontSize='large' />;
    }

    return (
      <TestTypeProvider>
        <IssueTestTypeProvider testCaseIds={this.state.testCaseIds}>
          <div ref={this.containerRef}>
            <NavigateButtons
              addTestCase={this.addTestCase}
              isAddingTestCase={this.state.isAddingTestCase}
              changeView={this.changeView}
              expandTestCases={this.expandTestCases}
              expanded={this.state.expanded}
              view={this.state.view}
              canEdit={this.context.canEdit}
            />
            <TestCases
              issueKey={this.state.issueKey}
              canEdit={this.context.canEdit}
              testCases={this.state.testCases}
              onUpdateTestCases={this.updateTestCases}
              expandOneTestCase={this.expandOneTestCase}
              syncDataFromFullScreen={this.syncDataFromFullScreen}
              onDeleteTestCaseLink={this.onDeleteTestCaseLink}
            />
          </div>
        </IssueTestTypeProvider>
      </TestTypeProvider>
    );
  }

  showLoading(): boolean {
    return this.state.isLoading || !this.context.currentUser;
  }

  // eslint-disable-next-line complexity
  render(): JSX.Element {
    const userIdentifier = this.context.currentUser
      ? this.context.currentUser.getIdentifier()
      : false;

    if (this.context.isConfigurationDone) {
      if (this.state.isFetchingError) {
        return this.getErrorFetchingBanner();
      }
      return this.getQAlityIssueViewContent();
    } else if (userIdentifier === 'anonymous_user' && !this.context.canEdit) {
      return this.getQAlityIssueViewContent();
    } else {
      return <Welcome />;
    }
    // TODO: if anonymous and can edit: ATESTM-96, low priority
  }
}
